"use client";

import Fuse from "fuse.js";
import { useMemo } from "react";

import { NoArtistInstrumentModelMixedModel } from "@/components/selectArtistModal/NoArtistInstrumentModelMixedModel";
import { FilterControl } from "@/components/selectArtistModal/royaltyFreeTab/filterControl/FilterControl";
import { SortArtists } from "@/components/selectArtistModal/royaltyFreeTab/filterControl/SortArtists";
import { VoiceGrid } from "@/components/selectArtistModal/VoiceGrid";
import { artists } from "@/constants/artists/artists";
import { allPitchRangeCategories } from "@/constants/selectArtistFilters";
import { artistFuseConfig } from "@/lib/fuse/fuseConfig";
import { useNamedSelectArtistFilters } from "@/lib/zustand/useSelectArtistFilters";
import { useTutorial } from "@/lib/zustand/useTutorial/useTutorial";
import { Artist, ArtistInstumentSaved, UserNoDates } from "@/types/types";
import { sortArtistsBySortVariant } from "@/utils/artistInstrumentModelMixedModel/sort/sortArtistsBySortVariant";
import { isFreeUser } from "@/utils/subscriptionLevels/isFreeUser";

const constructArtistPitchRangeCategories = (artist: Artist) => {
  const { low, high } = artist.filters.pitchRange.categories;

  const indexLow = allPitchRangeCategories.indexOf(low);
  const indexHigh = allPitchRangeCategories.indexOf(high);

  const artistPitchRangeCategories = allPitchRangeCategories.slice(
    indexLow,
    // +1 because slice is not inclusive
    indexHigh + 1
  );

  return artistPitchRangeCategories;
};

export function RoyaltyFreeTab({
  handleClickArtist,
  namespace,
  closeModal,
  user,
  savedArtistsInstrumentsIds,
  popularityByArtistId,
}: {
  handleClickArtist: (artistId: string) => void;
  namespace: string;
  closeModal: () => void;
  user: UserNoDates | null;
  savedArtistsInstrumentsIds: ArtistInstumentSaved["artistInstrumentId"][];
  // Weighted paid-user score per artist, computed daily by the compute-voice-popularity cron, fetched server-side by the page and drilled down. Null when the fetch failed — the "Popular" sort falls back to the default catalogue order.
  popularityByArtistId: Record<string, number> | null;
}) {
  // const [freeVoicesTop, setFreeVoicesTop] = useState(false);

  const useSelectArtistFilters = useNamedSelectArtistFilters((state) =>
    state.getNamedStore(namespace)
  );
  const genderFilterShow = useSelectArtistFilters(
    (state) => state.genderFilterShow
  );
  const pitchRangeCategoryFilterShow = useSelectArtistFilters(
    (state) => state.pitchRangeCategoryFilterShow
  );
  const genreFilterShow = useSelectArtistFilters(
    (state) => state.genreFilterShow
  );
  const searchString = useSelectArtistFilters((state) => state.searchString);
  const sortVariant = useSelectArtistFilters((state) => state.sortVariant);

  const isActive = useTutorial((state) => state.isActive);
  const artistId = useTutorial((state) => state.artistId);

  // const posthog = usePostHog();

  // A/B test Put free voices at the top useEffect(() => { const variant = posthog.getFeatureFlag("free-voices-top"); setFreeVoicesTop(variant === "test"); }, []);

  const artistsSortedBase = useMemo(
    () =>
      sortArtistsBySortVariant({ artists, sortVariant, popularityByArtistId }),
    [sortVariant, popularityByArtistId]
  );

  // Free / logged-out users see usable (free) voices first in the default view. The explicit Recent and Popular sorts opt out, so their ranking spans the whole catalogue — a new or popular paid voice shouldn't sit below every free one.
  const shouldFloatFreeVoicesFirst =
    sortVariant === "default" &&
    (user === null || isFreeUser(user.stripeSubscriptionId));

  const artistsFreeVoicesTop =
    shouldFloatFreeVoicesFirst ?
      [
        ...artistsSortedBase.filter((artist) => artist.meta.free),
        ...artistsSortedBase.filter((artist) => !artist.meta.free),
      ]
    : artistsSortedBase;

  // If tutorial is active, show the artist at the top of the list since the modal is not scrollable
  const artistsTutorialTop =
    isActive ?
      [
        ...artistsFreeVoicesTop.filter((artist) => artist.id === artistId),
        ...artistsFreeVoicesTop.filter((artist) => artist.id !== artistId),
      ]
    : artistsFreeVoicesTop;

  // Filter out artists based on gender
  const artistsFilteredOnGender = artistsTutorialTop.filter((artist) =>
    genderFilterShow.has(artist.filters.gender)
  );

  // Filter out artists based on pitch range category
  const artistsFilteredOnPitchRangeCategory = artistsFilteredOnGender.filter(
    (artist) => {
      const artistPitchRangeCategories =
        constructArtistPitchRangeCategories(artist);

      const isPitchRangeCategoryMatch = artistPitchRangeCategories.some(
        (category) => pitchRangeCategoryFilterShow.has(category)
      );

      return isPitchRangeCategoryMatch;
    }
  );

  // Filter out artists based on genre
  const artistsFilteredOnGenre = artistsFilteredOnPitchRangeCategory.filter(
    (artist) =>
      artist.filters.tags.primary.some((tag) => genreFilterShow.has(tag))
  );

  const fuse = useMemo(
    () => new Fuse(artistsFilteredOnGenre, artistFuseConfig),
    [artistsFilteredOnGenre]
  ); // Only recreate when filtered artists change

  const artistsFilteredOnSearch =
    searchString !== "" ?
      fuse.search(searchString).map((result) => result.item)
    : artistsFilteredOnGenre;

  // Check if no artists are shown
  const noArtists = artistsFilteredOnSearch.length === 0;

  return (
    <>
      <FilterControl namespace={namespace} className="mb-6 hidden sm:block" />
      <SortArtists
        namespace={namespace}
        listClassName="left-0 origin-top-left"
        className="mb-4 sm:hidden"
      />
      {noArtists ?
        <NoArtistInstrumentModelMixedModel text="No voices found. Try changing the filters or search term." />
      : <VoiceGrid
          voices={artistsFilteredOnSearch}
          handleClickArtist={(artistIdApp) => {
            handleClickArtist(artistIdApp);
            closeModal();
          }}
          user={user}
          savedArtistsInstrumentsIds={savedArtistsInstrumentsIds}
        />
      }
    </>
  );
}
